import * as React from 'react'

import { Box, Button, Tabs } from '@factorialco/deprecated-design-system'
import { PerformanceReviewerStrategiesStrategyEnumValues } from 'generated/resources/datatable.graphql'
import {
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceScoreConfigurationFragment,
  PerformanceScoreConfigurationSetWeightsDocument,
  PerformanceStrategyConfigurationInput,
  PerformanceValueObjectsQuestionnaireableEnum,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { FormProvider, useFormContext } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isEnumValue } from 'modules/core/lib/utils/enums'
import app from 'modules/core/store/models/app'

import { useMappedQuestionnaires } from 'modules/performance_process/hooks/useMappedQuestionnaires'
import { configurationByStrategy } from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'
import { QuestionnaireByReviewerStrategy } from 'modules/performance_process/lib/questionnaireForStrategies'
import {
  orderStrategies,
  strategyTranslation,
} from 'modules/performance_process/lib/reviewStrategy'

import { QuestionnaireWeightsPanel } from './QuestionnaireWeightsPanel'
import { StrategyConfigurationsPanel } from './StrategyConfigurationsPanel'

type TabType = PerformanceReviewerStrategiesStrategyEnum | 'total'
const TAB_TYPES: TabType[] = ['total', ...PerformanceReviewerStrategiesStrategyEnumValues]

const getTabs = (
  strategies: PerformanceReviewerStrategiesStrategyEnum[]
): { tabs: TabType[]; defaultTab: TabType } => {
  const usableStrategies = orderStrategies(strategies)

  return {
    tabs: ['total', ...usableStrategies],
    defaultTab: 'total',
  }
}

export type Form = {
  questionnaireableId: number
  questionnaireableType: PerformanceValueObjectsQuestionnaireableEnum
  configurationByStrategy: {
    [K in PerformanceReviewerStrategiesStrategyEnum]?: PerformanceStrategyConfigurationInput
  }
}

type SidepanelContentProps = {
  open: boolean
  reviewTypes: PerformanceReviewerStrategiesStrategyEnum[]
  questionnaires: QuestionnaireByReviewerStrategy
  hasCustomScoreConfigurationEnabled: boolean
  onClose: () => void
}
const SidepanelContent: React.FC<SidepanelContentProps> = ({
  open,
  reviewTypes,
  questionnaires,
  hasCustomScoreConfigurationEnabled,
  onClose,
}) => {
  const form = useFormContext<Form>()
  const { mappedQuestionnairesByStrategies } = useMappedQuestionnaires(questionnaires)
  const { tabs, defaultTab } = getTabs(reviewTypes)
  const [currentTab, setCurrentTab] = React.useState<TabType>(defaultTab)
  const [focusStrategy, setFocusStrategy] =
    React.useState<PerformanceReviewerStrategiesStrategyEnum | null>(null)

  const navigateToStrategyWeight = React.useCallback(
    (strategy: PerformanceReviewerStrategiesStrategyEnum) => {
      setCurrentTab('total')
      setFocusStrategy(strategy)
    },
    []
  )

  React.useEffect(() => {
    if (open) {
      setCurrentTab(defaultTab)
    }
  }, [open, defaultTab])

  const getTabLabel = (tab: TabType) => {
    return tab === 'total'
      ? i18n.t('performance_process.score_strategy_weights.total_tab')
      : strategyTranslation(tab)
  }

  const getTabTag = (tab: TabType) => {
    if (tab === 'total') {
      return ''
    }

    const strategyWeight = form.values.configurationByStrategy[tab]?.weight || 0
    return hasCustomScoreConfigurationEnabled ? `${strategyWeight}%` : ''
  }

  const handleTabClick = (id: string) => {
    if (!isEnumValue<TabType>(TAB_TYPES, id)) {
      return
    }
    if (id === 'total') {
      setFocusStrategy(null)
    }
    setCurrentTab(id)
  }

  const handleClose = () => {
    form.resetForm()
    onClose()
  }

  return (
    <Sidepanel
      open={open}
      onClose={handleClose}
      width="wideXL"
      title={i18n.t('performance_process.section_weights.sidepanel_title')}
      footer={
        <>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('performance_process.section_weights.sidepanel_cancel')}
          </Button>
          <Button onClick={() => form.handleSubmit()} loading={form.isSubmitting}>
            {i18n.t('performance_process.section_weights.sidepanel_save')}
          </Button>
        </>
      }
    >
      <Box width="full">
        <Tabs key={'question-preview-tabs'} currentId={currentTab} onTabClicked={handleTabClick}>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab} id={tab} label={getTabLabel(tab)} tag={getTabTag(tab)}>
              <Box paddingTop="s40">
                {tab === 'total' ? (
                  <StrategyConfigurationsPanel
                    focusStrategy={focusStrategy}
                    questionnaires={mappedQuestionnairesByStrategies}
                  />
                ) : (
                  <QuestionnaireWeightsPanel
                    strategy={tab}
                    questionnaire={mappedQuestionnairesByStrategies[tab]}
                    navigateToStrategyWeight={navigateToStrategyWeight}
                  />
                )}
              </Box>
            </Tabs.Tab>
          ))}
        </Tabs>
      </Box>
    </Sidepanel>
  )
}

type Props = SidepanelContentProps & {
  questionnaireableId: number
  questionnaireableType: PerformanceValueObjectsQuestionnaireableEnum
  scoreConfiguration?: PerformanceScoreConfigurationFragment | null
}

export const ScoreWeightsSidepanel: React.FC<Props> = ({
  questionnaireableId,
  questionnaireableType,
  scoreConfiguration,
  hasCustomScoreConfigurationEnabled,
  ...props
}) => {
  const defaultValues = {
    hasCustomScoreConfigurationEnabled,
    questionnaireableId: questionnaireableId,
    questionnaireableType: questionnaireableType,
    configurationByStrategy: configurationByStrategy(scoreConfiguration),
  }

  const [setWeights] = useMutation(PerformanceScoreConfigurationSetWeightsDocument, {
    extractorFn: ({ performanceMutations }) =>
      performanceMutations.setWeightsPerformanceScoreConfiguration,
  })

  const handleSubmit = async (data: Form) => {
    const result = await setWeights({
      variables: {
        questionnaireableId: questionnaireableId,
        questionnaireableType: questionnaireableType,
        strategyConfigurations: Object.values(data.configurationByStrategy),
      },
    })
    const handled = handleResult(result, () => null)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }
  return (
    <FormProvider defaultValues={defaultValues} onSubmit={handleSubmit}>
      <SidepanelContent
        {...props}
        hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
      />
    </FormProvider>
  )
}
